/**
 * topologyWalk.ts — Pass 1 (CONTENT) walker for the v0.2 layout pipeline.
 *
 * Two public exports:
 *   - `discoverAnchors(nodes, edges)` returns `{ spineAnchors, anchorRoots }`
 *     — anchor discovery for layoutDag.
 *   - `walkChildrenFrom(parent, x, y, depth, visited, ctx)` (+ the
 *     `makeWalker(ctx)` curried form per plan-triad N3) — recurses
 *     children, dispatches by `NODE_GROUP`, returns the new bottom-y
 *     cursor.
 *
 * Walker dispatches by `NODE_GROUP[child.type]`:
 *   - LATERAL: stacked right of parent via `layoutLateralSubtree`
 *     + recursive walk into each lateral subtree (peer fans, etc.)
 *   - SPINE: 0 / 1 / N branching. N>1 with N>=SPINE_FAN_COLLAPSE_THRESHOLD
 *     auto-collapses to a synthetic `spine_fan_chip` unless the operator
 *     has expanded via `runtime.collapsedFanIds` (key: `${parent.id}-fanchip`).
 *   - DECORATION: skipped here — handled by Pass 2 `decorationPass`.
 *
 * Cycle bailout: MAX_DEPTH guard emits a `cycle_error` decoration node
 * AT (x, y) of the bailout — auto-clears on next render. Warn fires via
 * the shared `chatDagWarn('cycle_bailout', ...)` helper (rate-limited).
 */
import type { Node, Edge } from 'reactflow';

import { NODE_GROUP, type ChatDagNodeType } from '../types';
import {
  MAX_DEPTH,
  SPINE_FAN_COLLAPSE_THRESHOLD,
  CONSTANTS,
  layoutPolarArc,
  layoutPolarArcDownward,
  computeArcRadius,
} from './layoutFans';
import { chatDagWarn } from './warnTelemetry';

export type WalkCtx = {
  childrenByParent: Map<string, string[]>;
  byId: Map<string, Node>;
  dimsOf: (n: Node) => { w: number; h: number };
  nodes: Node[];                              // walker may push synthetic chips / cycle_errors
  runtime?: { collapsedFanIds?: ReadonlySet<string> };
};

/**
 * discoverAnchors — partitions input nodes into two anchor sets.
 *
 *   anchorRoots: NODE_GROUP[kind] === 'ANCHORS' (e.g. spine_root_marker),
 *                sorted by invitedAt then id.
 *   spineAnchors: (nodes \ anchorRoots) where NODE_GROUP[kind] in
 *                {SPINE, LATERAL} AND no in-set parent — i.e. roots
 *                of the chronological spine. Sorted by timestamp then id.
 *
 * Returned as an object so callers destructure (plan-triad P2 fix).
 */
export function discoverAnchors(
  nodes: Node[],
  edges: Edge[],
): { spineAnchors: Node[]; anchorRoots: Node[] } {
  const anchorRoots: Node[] = nodes
    .filter((n) => n.type && NODE_GROUP[n.type as ChatDagNodeType] === 'ANCHORS')
    .sort((a, b) => {
      const aInv = ((a.data as any)?.invitedAt) ?? '';
      const bInv = ((b.data as any)?.invitedAt) ?? '';
      if (aInv !== bInv) return aInv < bInv ? -1 : 1;
      return a.id < b.id ? -1 : 1;
    });
  const anchorRootIds = new Set(anchorRoots.map((n) => n.id));

  const parentsByChild = new Map<string, Set<string>>();
  const nodeIds = new Set(nodes.map((n) => n.id));
  for (const e of edges) {
    if (!parentsByChild.has(e.target)) parentsByChild.set(e.target, new Set());
    parentsByChild.get(e.target)!.add(e.source);
  }

  const spineAnchors: Node[] = nodes
    .filter((n) => {
      if (anchorRootIds.has(n.id)) return false;
      const kind = n.type as ChatDagNodeType | undefined;
      if (!kind) return false;
      const group = NODE_GROUP[kind];
      if (group !== 'SPINE' && group !== 'LATERAL') return false;
      const parents = parentsByChild.get(n.id);
      if (!parents) return true;
      for (const p of parents) {
        if (nodeIds.has(p)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const at = ((a.data as any)?.timestamp) ?? '';
      const bt = ((b.data as any)?.timestamp) ?? '';
      if (at !== bt) return at < bt ? -1 : 1;
      return a.id < b.id ? -1 : 1;
    });

  return { spineAnchors, anchorRoots };
}

/**
 * chooseGap — pick the vertical gap for a SPINE child placement based
 * on parent + child shape. v0.2 walker dispatches three gaps:
 *
 *   TURN_VERTICAL_GAP (64) — asst turn_marker -> user turn_marker (turn boundary)
 *   ASST_PRELUDE_GAP (56)  — synthesis llm_round -> asst turn_marker (delivery slot)
 *   ROUND_VERTICAL_GAP (22) — everything else (round->round, peer->peer chains, etc.)
 *
 * Without this, every spine transition reused ROUND_VERTICAL_GAP and
 * the trail packed too tightly at turn boundaries.
 */
function chooseGap(parent: Node, child: Node): number {
  const pIsAsstMarker =
    parent.type === 'turn_marker' && (parent.data as any)?.role === 'assistant';
  const cIsUserMarker =
    child.type === 'turn_marker' && (child.data as any)?.role === 'user';
  if (pIsAsstMarker && cIsUserMarker) return CONSTANTS.TURN_VERTICAL_GAP;

  const pIsSynthRound =
    parent.type === 'llm_round' && (parent.data as any)?.isSynthesis === true;
  const cIsAsstMarker =
    child.type === 'turn_marker' && (child.data as any)?.role === 'assistant';
  if (pIsSynthRound && cIsAsstMarker) return CONSTANTS.ASST_PRELUDE_GAP;

  return CONSTANTS.ROUND_VERTICAL_GAP;
}

function synthesizeSpineFanChip(
  parentFanKey: string,
  spine: Node[],
  parentMessageId: string,
  flavor: 'proposals' | 'squad' | 'chorus' = 'proposals',
): Node {
  return {
    id: `chip-${parentFanKey}`,
    type: 'spine_fan_chip',
    data: {
      kind: 'spine_fan_chip',
      parentMessageId,
      parentFanKey,
      childCount: spine.length,
      personaIds: spine
        .map((s) => ((s.data as any)?.personaId))
        .filter((p): p is string => typeof p === 'string'),
      // SP3-MF-06 — backfill flavor so existing proposal fans keep their
      // semantic label. ChatDAGRAGDAG squad-context fans set 'squad' at
      // their own emit site (chatDagDataProcessor or here-with-context
      // when a squad-spine-fan path lands).
      flavor,
    },
    position: { x: 0, y: 0 },
  } as Node;
}

/**
 * makeWalker — closure-captured ctx per plan-triad N3.
 *
 * Returns a `walk(parent, x, y, depth, visited)` function that uses
 * the ctx for adjacency, dim lookups, and mutation surface. Avoids
 * passing 9 args every call.
 */
export function makeWalker(ctx: WalkCtx) {
  function walk(
    parent: Node,
    x: number,
    y: number,
    depth: number,
    visited: Set<string>,
  ): number {
    if (depth > MAX_DEPTH) {
      const cycleKey = `cycle:${parent.id}`;
      if (!visited.has(cycleKey)) {
        visited.add(cycleKey);
        chatDagWarn('cycle_bailout', parent.id, { parentId: parent.id, depth });
        ctx.nodes.push({
          id: `cycle-error-${parent.id}`,
          type: 'cycle_error',
          data: {
            kind: 'cycle_error',
            parentId: parent.id,
            position: { x, y },
          },
          position: { x, y },
        } as Node);
      }
      return y;
    }
    if (visited.has(parent.id)) return y;
    visited.add(parent.id);

    const childIds = ctx.childrenByParent.get(parent.id) ?? [];
    const children = childIds
      .map((id) => ctx.byId.get(id))
      .filter((n): n is Node => !!n);

    const lateral = children.filter((c) => {
      const k = c.type as ChatDagNodeType | undefined;
      return !!k && NODE_GROUP[k] === 'LATERAL';
    });
    const spine = children.filter((c) => {
      const k = c.type as ChatDagNodeType | undefined;
      return !!k && NODE_GROUP[k] === 'SPINE';
    });

    const { w: parentW, h: parentH } = ctx.dimsOf(parent);

    let lateralBottomY = y + parentH;
    if (lateral.length > 0) {
      // Peer fan (consult_peers tool_call -> peer cards): polar arc to
      // the right of the tool. Matches v0.1 layoutChatDag behavior;
      // detected when parent is a tool_call AND every child is 'peer'.
      const isPeerFan =
        parent.type === 'tool_call' && lateral.every((c) => c.type === 'peer');
      if (isPeerFan) {
        const maxPeerH = Math.max(...lateral.map((p) => ctx.dimsOf(p).h));
        const radius = computeArcRadius(lateral.length, maxPeerH);
        const anchorX = x + parentW + CONSTANTS.TOOL_HORIZONTAL_GAP;
        const anchorY = y + parentH / 2;
        const positions = layoutPolarArc(lateral.length, radius, anchorX, anchorY);
        for (let i = 0; i < lateral.length; i++) {
          const { w: pw, h: ph } = ctx.dimsOf(lateral[i]);
          // Center the peer card on the arc point.
          const px = positions[i].x - pw / 2;
          const py = positions[i].y - ph / 2;
          lateral[i].position = { x: px, y: py };
          const subBottom = walk(lateral[i], px, py, depth + 1, visited);
          lateralBottomY = Math.max(lateralBottomY, subBottom, py + ph);
        }
      } else {
        // Lateral stack — position one at a time, each lateral's CURSOR
        // advances past its subtree bottom (not just its own height)
        // so the next lateral clears the prior's full subtree
        // (architect catch on spec triad 2).
        //
        // x = parent.x + parent.w + TOOL_HORIZONTAL_GAP — right of the
        // PARENT'S RIGHT EDGE, NOT parent.x + LATERAL_OFFSET. LATERAL_OFFSET
        // alone (56) puts a tool_call (280 wide) at x=56, well inside a
        // 220-wide llm_round at x=0 — the tool's left edge visually overlaps
        // the round's right portion (the bug the operator screenshotted).
        const lateralX = x + parentW + CONSTANTS.TOOL_HORIZONTAL_GAP;
        let cursorY = y;
        for (const l of lateral) {
          const { h: laterH } = ctx.dimsOf(l);
          l.position = { x: lateralX, y: cursorY };
          const subBottom = walk(l, l.position.x, l.position.y, depth + 1, visited);
          lateralBottomY = Math.max(lateralBottomY, subBottom, cursorY + laterH);
          cursorY = Math.max(cursorY + laterH, subBottom) + CONSTANTS.TOOL_VERTICAL_GAP;
        }
      }
    }

    if (spine.length === 0) {
      // No spine child to place — return content bottom + a small breath
      // so the next sibling at the caller's level clears this subtree.
      return Math.max(y + parentH, lateralBottomY) + CONSTANTS.ROUND_VERTICAL_GAP;
    }

    if (spine.length === 1) {
      const child = spine[0];
      const gap = chooseGap(parent, child);
      const childY = Math.max(y + parentH, lateralBottomY) + gap;
      child.position = { x, y: childY };
      return walk(child, x, childY, depth + 1, visited);
    }

    // N > 1 — sibling fan. Gap on the fan's anchor row uses the same
    // dispatch (homogeneous siblings — first child as the probe).
    const sampleGap = chooseGap(parent, spine[0]);
    const fanAnchorY =
      Math.max(y + parentH, lateralBottomY) + sampleGap;

    const parentFanKey = `${parent.id}-fanchip`;
    const toggled = !!ctx.runtime?.collapsedFanIds?.has(parentFanKey);

    // A7 (SP-1) — squad-context fans (member peer_responses and/or the merge)
    // default to EXPANDED; the operator's toggle inverts the default (so they
    // can still collapse). Non-squad fans keep the legacy collapse-at-threshold
    // default. collapsedFanIds membership = "operator overrode the default".
    const hasSquadContext = spine.some(
      (c) =>
        (c.type === 'peer_response' && !!(c.data as any)?.squadId) ||
        c.type === 'squad_merge',
    );
    // TH-4 Three-Read Chorus — a chorus of reads (peer_responses carrying
    // chorusStance) defaults to EXPANDED like a squad, so the reads + the
    // divergent-read marker + ⤴ promote stay visible at the arc's namesake N=3
    // (a plain spine fan would collapse to a chip at SPINE_FAN_COLLAPSE_THRESHOLD).
    const hasChorusContext = spine.some(
      (c) => c.type === 'peer_response' && !!(c.data as any)?.chorusStance,
    );
    const defaultCollapsed = (hasSquadContext || hasChorusContext)
      ? false
      : spine.length >= SPINE_FAN_COLLAPSE_THRESHOLD;
    const shouldCollapse = defaultCollapsed !== toggled;   // toggle inverts default

    if (shouldCollapse) {
      const chip = synthesizeSpineFanChip(
        parentFanKey, spine, parent.id,
        hasSquadContext ? 'squad' : hasChorusContext ? 'chorus' : 'proposals',
      );
      chip.position = { x, y: fanAnchorY };
      ctx.nodes.push(chip);
      const { h: chipH } = ctx.dimsOf(chip);
      return fanAnchorY + chipH;
    }

    const maxChildH = Math.max(...spine.map((s) => ctx.dimsOf(s).h));
    const radius = computeArcRadius(spine.length, maxChildH);
    const positions = layoutPolarArcDownward(spine.length, radius, x, fanAnchorY);

    let maxBottom = fanAnchorY;
    for (let i = 0; i < spine.length; i++) {
      spine[i].position = { x: positions[i].x, y: positions[i].y };
      const bottom = walk(
        spine[i],
        positions[i].x,
        positions[i].y,
        depth + 1,
        visited,
      );
      maxBottom = Math.max(maxBottom, bottom);
    }
    return maxBottom;
  }
  return walk;
}

/** One-shot walker invocation (compat for callers that don't need the curried form). */
export function walkChildrenFrom(
  parent: Node,
  x: number,
  y: number,
  depth: number,
  visited: Set<string>,
  ctx: WalkCtx,
): number {
  return makeWalker(ctx)(parent, x, y, depth, visited);
}
